﻿import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Droplets, ArrowLeft, Upload, Download, Pencil, Trash2, Box } from 'lucide-react';
import axios from 'axios';

/** Pull a human-readable error message from an axios/server error, falling back to a default. */
const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      // ASP.NET ProblemDetails / ValidationProblemDetails / custom ErrorResponse
      const obj = data as Record<string, unknown>;
      if (typeof obj.errorMessage === 'string') return obj.errorMessage;
      if (typeof obj.title === 'string') return obj.title;
      if (typeof obj.detail === 'string') return obj.detail;
      if (typeof obj.message === 'string') return obj.message;
    }
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

/** Returns the PNG icon path for a given file extension (matching V2 icons) */
const getFileIcon = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const supported = ['avi','csv','doc','exe','jpg','las','mp4','pdf','ply','png','tiff','txt','xls','zip'];
  const icon = supported.includes(ext) ? ext : 'file';
  return `/icons/ico-${icon}.png`;
};
import { FormField, Input, Select, Button, Toast } from '../../components/ui/FormField';
import { cn } from '../../lib/utils';
import { wellApi, companyApi, siteApi, toolApi, annotationApi, wellAnnotationApi, cloudProcessApi } from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import type { Well, Company, Site, Tool, Annotation, UploadFileItem, GeonomicFileItem, CloudProcessItem } from '../../types/api';

/** Deduplicate LAS files by name, keeping only the latest per filename (matching V2) */
const getLatestLasFiles = (files: GeonomicFileItem[]): GeonomicFileItem[] => {
  const latestByName = new Map<string, GeonomicFileItem>();
  files.forEach((f) => {
    const name = f.fileName?.toLowerCase() ?? '';
    if (!name.endsWith('.las')) return;
    const existing = latestByName.get(name);
    if (!existing || (f.id > existing.id)) {
      latestByName.set(name, f);
    }
  });
  return Array.from(latestByName.values());
};

const emptyWell: Partial<Well> = {
  wellName: '', fieldName: '', frm: '', location: '', county: '', province: '', country: '',
  wellId: '', licenceNumber: '', serviceCompany: '', latitude: '', longitude: '', notes: '',
  afe: '', rig: '', son: '', bs: '', cbl: '', dft: '', dfd: '', dfv: '', tdh: '', tch: '', thbr: '',
  startDepth: 0, stopDepth: 0, perigonSensorOffsetToBit: null,
  companyId: 0, siteId: 0, toolId: null,
};

export function WellFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;

  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const isCompanyAdmin = authUser?.role === 'CompanyAdmin';
  const canSeeInternalDetails = isSuperAdmin || isCompanyAdmin;
  // Panels explicitly labelled "(Internal Only)" — Technical Details and LAS Files.
  // CompanyAdmin is a customer-side role, so it is SuperAdmin only.
  const canSeeInternalOnly = isSuperAdmin;
  // Only SuperAdmin may add/edit wells; company roles get a read-only view.
  const canEdit = isSuperAdmin;

  // In the read-only customer view an empty field should simply look empty.
  // "e.g. …" hints and "-- Select --" prompts are authoring aids and read as
  // real data to someone who can't edit, so drop them.
  const ph = (example: string) => (canEdit ? example : undefined);
  const emptyOption = (label: string) => (canEdit ? label : '');

  // Block non-admins from the create form (direct /wells/new URL guard).
  useEffect(() => {
    if (!isEdit && !canEdit) navigate('/dashboard/wells', { replace: true });
  }, [isEdit, canEdit, navigate]);

  const siteIdParam = Number(searchParams.get('siteId') ?? 0);
  const siteNameParam = searchParams.get('siteName') ?? '';
  const filterCompanyId = Number(searchParams.get('companyId') ?? 0);

  const [form, setForm] = useState<Partial<Well>>(emptyWell);
  const [wellCompanyId, setWellCompanyId] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [tools, setTools] = useState<Tool[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' } | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Annotations
  const [wellAnnotations, setWellAnnotations] = useState<{ id: number; annotationId: number; shortDescription: string; longDescription: string; depth: number }[]>([]);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState('');
  const [annotationDepth, setAnnotationDepth] = useState<number | ''>('');
  const [annotationEditId, setAnnotationEditId] = useState<number | null>(null);

  // Files
  const [uploadedFiles, setUploadedFiles] = useState<UploadFileItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const [lasFiles, setLasFiles] = useState<GeonomicFileItem[]>([]);
  const [uploadingLas, setUploadingLas] = useState(false);
  const [lasUploadProgress, setLasUploadProgress] = useState(0);
  const lasFileInputRef = useRef<HTMLInputElement>(null);
  const lasUploadAbortRef = useRef<AbortController | null>(null);
  const [plyFiles, setPlyFiles] = useState<CloudProcessItem[]>([]);
  const [downloadingPly, setDownloadingPly] = useState<string | null>(null);

  // Create well from LAS (create mode only)
  const [creatingFromLas, setCreatingFromLas] = useState(false);
  const createLasFileInputRef = useRef<HTMLInputElement>(null);
  const [isLasDragOver, setIsLasDragOver] = useState(false);
  // Create-from-LAS progress: 'uploading' shows a determinate bar (file transfer %),
  // then 'processing' shows an indeterminate bar while the server parses the LAS and
  // creates the well(s) — that step reports no percentage.
  const [lasCreateProgress, setLasCreateProgress] = useState(0);
  const [lasCreatePhase, setLasCreatePhase] = useState<'uploading' | 'processing'>('uploading');
  const lasCreateAbortRef = useRef<AbortController | null>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
  };

  // Load reference data
  useEffect(() => {
    companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? [])).catch(() => {});
    toolApi.getAll({ limit: 1000 }).then((r) => setTools(r.entities ?? [])).catch(() => {});
    annotationApi.getAll({ limit: 1000 }).then((r) => setAnnotations(r.entities ?? [])).catch(() => {});
  }, []);

  // Load well for edit
  useEffect(() => {
    if (!isEdit || !id) return;
    wellApi.getById(Number(id))
      .then((r) => {
        const well = r.entity ?? r;
        setForm({ ...well });
        setWellCompanyId((well as Well).companyId ?? 0);
        // getAllActive is SuperAdmin-only, so CompanyUser/CompanyAdmin get an empty company
        // list and the dropdown can't render the well's company. Seed it from the well payload.
        // getbyidwell returns different shapes by role: SuperAdmin gets a nested `company`
        // object; company roles get a limited view model with flat companyId + companyName.
        const w = well as Well & { companyName?: string };
        const seedCompany: Company | null =
          w.company ??
          (w.companyId ? ({ id: w.companyId, companyName: w.companyName ?? '' } as Company) : null);
        if (seedCompany) {
          setCompanies((prev) => prev.some((c) => c.id === seedCompany.id) ? prev : [seedCompany, ...prev]);
        }
        // Load annotations via dedicated endpoint (getByIdWell doesn't include them)
        wellAnnotationApi.getByWellId(Number(id))
          .then((ar) => {
            setWellAnnotations((ar.entities ?? []).map((a) => ({
              id: a.id,
              annotationId: a.annotationId,
              shortDescription: a.shortDescription ?? '',
              longDescription: a.longDescription ?? '',
              depth: a.depth,
            })));
          })
          .catch(() => {});
        // load LAS files (from well model) + client downloads (dedicated endpoint)
        wellApi.getWellServiceModel(Number(id))
          .then((fr) => {
            if (fr.status === 'Failed' || fr.error) return;
            const entity = fr.entity;
            const geoFiles = entity?.geonomicFiles ?? [];
            setLasFiles(getLatestLasFiles(geoFiles.filter((f) => f.geoFileType === 1)));
          })
          .catch(() => showToast('Failed to load files', 'error'));
        loadClientDownloads(Number(id), (well as Well).companyId ?? 0);
        // load 3D models
        loadPlyFiles(Number(id));
      })
      .catch(() => showToast('Failed to load well', 'error'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // Load sites when company changes
  useEffect(() => {
    if (form.companyId) {
      siteApi.getAllByCompanyId(Number(form.companyId), { limit: 1000 })
        .then((r) => setSites(r.entities ?? [])).catch(() => {});
    } else {
      setSites([]);
    }
  }, [form.companyId]);

  // Pre-set company/site for new wells
  useEffect(() => {
    if (!isEdit) {
      setForm((prev) => ({
        ...prev,
        companyId: isSuperAdmin ? (filterCompanyId || 0) : Number(authUser?.companyId ?? 0),
        siteId: siteIdParam || 0,
      }));
    }
  }, [isEdit, isSuperAdmin, filterCompanyId, authUser?.companyId, siteIdParam]);

  const set = (key: string, value: string | number | null) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const goBack = () => {
    if (siteIdParam > 0) {
      navigate(`/dashboard/wells?siteId=${siteIdParam}&siteName=${encodeURIComponent(siteNameParam)}`);
    } else {
      navigate('/dashboard/wells');
    }
  };

  // Validation (mirrors V2 well-register required fields)
  const errors = {
    companyId: !form.companyId ? 'Company is required' : '',
    siteId: !form.siteId ? 'Surface Location is required' : '',
    wellName: !form.wellName?.trim() ? 'Well Name is required' : '',
    wellId: !form.wellId?.trim() ? 'UWI is required' : '',
    licenceNumber: !form.licenceNumber?.trim() ? 'Licence Number is required' : '',
  };
  const isInvalid = Object.values(errors).some((e) => !!e);

  const handleSave = async () => {
    setSubmitted(true);
    if (isInvalid) {
      const first = Object.values(errors).find((e) => !!e);
      showToast(first || 'Please fill in all required fields', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = { well: form, GeonomicFiles: [] };
      if (isEdit) {
        await wellApi.update(payload);
        showToast('Well updated successfully');
      } else {
        await wellApi.create(payload);
        showToast('Well created successfully');
      }
      setTimeout(goBack, 800);
    } catch {
      showToast('Failed to save well', 'error');
      setSaving(false);
    }
  };

  // CloudProcess depth range for annotation validation (matching V2)
  const minValidDepth = plyFiles.length > 0 ? Math.min(...plyFiles.map(p => p.fromLength)) : null;
  const maxValidDepth = plyFiles.length > 0 ? Math.max(...plyFiles.map(p => p.toLength)) : null;

  // 3D models whose .ply files actually exist on disk. The server flags each
  // cloud type; treat an absent flag (older API) as present so nothing is hidden
  // unexpectedly. A model with neither file is omitted entirely.
  const visiblePlyFiles = plyFiles.filter(f => f.hasActualFile !== false || f.hasPerfectFile !== false);

  // Annotations helpers
  const addWellAnnotation = async () => {
    if (!selectedAnnotationId || annotationDepth === '' || Number(annotationDepth) <= 0) {
      showToast('Please select an annotation and enter a valid depth', 'error');
      return;
    }
    // Validate CloudProcess records exist
    if (minValidDepth === null || maxValidDepth === null) {
      showToast('Cannot add annotation. This well must have at least one active Cloud Process record.', 'error');
      return;
    }
    // Validate depth is within CloudProcess range
    if (Number(annotationDepth) < minValidDepth || Number(annotationDepth) > maxValidDepth) {
      showToast(`Depth must be between ${minValidDepth} and ${maxValidDepth} based on Cloud Process records`, 'error');
      return;
    }
    const ann = annotations.find((a) => a.id === Number(selectedAnnotationId));
    if (!ann) return;
    if (annotationEditId !== null) {
      try {
        await wellAnnotationApi.update({
          id: annotationEditId,
          wellId: Number(id),
          annotationId: Number(selectedAnnotationId),
          depth: Number(annotationDepth),
          shortDescription: ann.shortDescription,
          longDescription: ann.longDescription,
        });
        setWellAnnotations((prev) => prev.map((wa) => wa.id === annotationEditId
          ? { ...wa, annotationId: Number(selectedAnnotationId), shortDescription: ann.shortDescription, longDescription: ann.longDescription, depth: Number(annotationDepth) }
          : wa));
        setAnnotationEditId(null);
      } catch {
        showToast('Failed to update annotation', 'error');
      }
    } else {
      try {
        const result = await wellAnnotationApi.add({
          wellId: Number(id),
          annotationId: Number(selectedAnnotationId),
          depth: Number(annotationDepth),
          shortDescription: ann.shortDescription,
          longDescription: ann.longDescription,
        });
        const newId = result.entity?.id ?? Date.now();
        setWellAnnotations((prev) => [...prev, { id: newId, annotationId: Number(selectedAnnotationId), shortDescription: ann.shortDescription, longDescription: ann.longDescription, depth: Number(annotationDepth) }]);
      } catch {
        showToast('Failed to add annotation', 'error');
      }
    }
    setSelectedAnnotationId('');
    setAnnotationDepth('');
  };

  const removeWellAnnotation = async (wid: number) => {
    try {
      await wellAnnotationApi.delete(wid);
      setWellAnnotations((prev) => prev.filter((wa) => wa.id !== wid));
    } catch {
      showToast('Failed to delete annotation', 'error');
    }
  };

  const editWellAnnotation = (wid: number) => {
    const wa = wellAnnotations.find((a) => a.id === wid);
    if (!wa) return;
    setSelectedAnnotationId(String(wa.annotationId));
    setAnnotationDepth(wa.depth);
    setAnnotationEditId(wid);
  };

  // File helpers
  const loadClientDownloads = (wellId: number, companyId: number) => {
    if (!companyId) return;
    wellApi.getClientDownloads(companyId, wellId)
      .then((r) => setUploadedFiles(r.entities ?? []))
      .catch(() => {});
  };

  const loadFiles = (wellId: number) => {
    wellApi.getWellServiceModel(wellId)
      .then((r) => {
        if (r.status === 'Failed' || r.error) {
          showToast('Failed to refresh file list', 'error');
          return;
        }
        const entity = r.entity;
        const geoFiles = entity?.geonomicFiles ?? [];
        setLasFiles(getLatestLasFiles(geoFiles.filter((f) => f.geoFileType === 1)));
      }).catch(() => showToast('Failed to refresh file list', 'error'));
    loadClientDownloads(wellId, wellCompanyId || Number(form.companyId));
  };

  const loadPlyFiles = (wellId: number) => {
    cloudProcessApi.getAllByWellId(wellId)
      .then((r) => {
        if (r.status === 'Failed' || r.error) return;
        setPlyFiles((r.entities ?? []).filter((p) => !p.isDeleted));
      }).catch(() => {});
  };

  const open3DModel = (file: CloudProcessItem) => {
    const token = localStorage.getItem('geo_access_token') || '';
    const viewerBase = import.meta.env.VITE_3D_VIEWER_URL || 'https://gviewtest.geonomictech.com';
    const url = `${viewerBase}/?fileId=${file.id}&wellId=${Number(id)}&token=${token}`;
    window.open(url, '_blank');
  };

  const handlePlyDownload = async (file: CloudProcessItem, cloudType: 'A' | 'P') => {
    const suffix = cloudType === 'A' ? 'Actual' : 'Perfect';
    const fileName = `${file.wellName}_${file.fromLength}m-${file.toLength}m_${suffix}_${file.id}.ply`;
    setDownloadingPly(fileName);
    try {
      const blob = await cloudProcessApi.downloadPlyFile(file.id, cloudType);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast(`Failed to download ${fileName}`, 'error');
    } finally {
      setDownloadingPly(null);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !isEdit) return;
    const cId = wellCompanyId || Number(form.companyId);
    if (!cId) { showToast('Company ID not loaded yet — please wait', 'error'); return; }
    setUploading(true);
    setUploadProgress(0);
    const abort = new AbortController();
    uploadAbortRef.current = abort;
    try {
      await wellApi.uploadClientDownloadChunked(
        file, cId, Number(id),
        (pct) => setUploadProgress(pct),
        abort.signal,
      );
      showToast(`${file.name} uploaded successfully`);
      loadFiles(Number(id));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        showToast('Upload cancelled', 'error');
      } else {
        showToast('Failed to upload file', 'error');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      uploadAbortRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (fileName: string) => {
    const cId = wellCompanyId || Number(form.companyId);
    setDownloadingFile(fileName);
    try {
      const blob = await wellApi.downloadClientDownload(fileName, cId, Number(id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Failed to download file', 'error');
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleDeleteFile = async (fileName: string) => {
    const cId = wellCompanyId || Number(form.companyId);
    if (!confirm(`Delete file "${fileName}"?`)) return;
    try {
      await wellApi.deleteClientDownload(fileName, cId, Number(id));
      showToast(`${fileName} deleted`);
      loadFiles(Number(id));
    } catch {
      showToast('Failed to delete file', 'error');
    }
  };

  const handleCreateWellFromLas = async (file: File | undefined | null) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.las')) {
      showToast('Please select a valid .las file', 'error');
      return;
    }
    const cId = Number(form.companyId);
    if (!cId) { showToast('Please select a Company first', 'error'); return; }
    const sId = Number(form.siteId) || siteIdParam || 0;
    setCreatingFromLas(true);
    setLasCreateProgress(0);
    setLasCreatePhase('uploading');
    const abort = new AbortController();
    lasCreateAbortRef.current = abort;
    try {
      const result = await wellApi.uploadLas(
        file, cId, sId,
        (pct) => {
          setLasCreateProgress(pct);
          // Transfer done — the server is now parsing the LAS and creating wells.
          if (pct >= 100) setLasCreatePhase('processing');
        },
        abort.signal,
      );
      if (result?.status === 'Successfull' && (result.entities?.length ?? 0) > 0) {
        showToast(`${file.name} uploaded — well(s) created`);
        setTimeout(goBack, 800);
      } else {
        showToast(result?.error?.errorMessage || 'Failed to create well from LAS', 'error');
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        showToast('Upload cancelled', 'error');
      } else {
        showToast(extractErrorMessage(err, 'Failed to upload LAS file'), 'error');
      }
    } finally {
      setCreatingFromLas(false);
      setLasCreateProgress(0);
      setLasCreatePhase('uploading');
      lasCreateAbortRef.current = null;
      if (createLasFileInputRef.current) createLasFileInputRef.current.value = '';
    }
  };

  const handleLasUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !isEdit) return;
    const cId = wellCompanyId || Number(form.companyId);
    setUploadingLas(true);
    setLasUploadProgress(0);
    const abort = new AbortController();
    lasUploadAbortRef.current = abort;
    try {
      await wellApi.uploadLasForWell(
        file, Number(id), cId,
        (pct) => setLasUploadProgress(pct),
        abort.signal,
      );
      showToast(`${file.name} uploaded successfully`);
      loadFiles(Number(id));
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        showToast('Upload cancelled', 'error');
      } else {
        showToast(extractErrorMessage(err, 'Failed to upload LAS file'), 'error');
      }
    } finally {
      setUploadingLas(false);
      setLasUploadProgress(0);
      lasUploadAbortRef.current = null;
      if (lasFileInputRef.current) lasFileInputRef.current.value = '';
    }
  };

  const handleLasDelete = async (f: GeonomicFileItem) => {
    if (!confirm(`Delete LAS file "${f.fileName}"?`)) return;
    try {
      await wellApi.update({
        well: form,
        GeonomicFiles: [{ id: f.id, isDeleted: true, geoFileType: 1, fileName: f.fileName, fileType: f.fileType, contentType: f.contentType, companyId: f.companyId }],
      });
      showToast(`${f.fileName} deleted`);
      loadFiles(Number(id));
    } catch {
      showToast('Failed to delete LAS file', 'error');
    }
  };

  const handleLasDownload = async (f: { id: number; fileName: string }) => {
    const cId = wellCompanyId || Number(form.companyId);
    setDownloadingFile(f.fileName);
    try {
      const blob = await wellApi.downloadLasFile(f.id, cId, Number(id));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = f.fileName; a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Failed to download LAS file', 'error');
    } finally {
      setDownloadingFile(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={goBack}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            title="Back to Wells"
          >
            <ArrowLeft size={20} />
          </button>
          <Droplets className="h-7 w-7 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {!canEdit ? 'Well Details' : (isEdit ? 'Edit Well' : 'Add Well')}
            </h1>
            {siteIdParam > 0 && (
              <p className="text-sm text-gray-500">
                Location: <span className="font-medium text-gray-700">{siteNameParam}</span>
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={goBack}>{canEdit ? 'Cancel' : 'Back'}</Button>
          {canEdit && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Well'}
            </Button>
          )}
        </div>
      </div>

      {/* Two-column layout: left = form + annotations, right = files. Even 50/50 split. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Read-only view: keep the inputs white with normal text rather than the
          default greyed-out disabled look — this is a data view, not a blocked form.
          Scoped to input/select/textarea on purpose: a bare :disabled selector would
          also strip the dimming from disabled buttons and make them look clickable. */}
      <fieldset
        disabled={!canEdit}
        className={cn(
          'min-w-0',
          !canEdit && [
            '[&_input:disabled]:bg-white [&_select:disabled]:bg-white [&_textarea:disabled]:bg-white',
            '[&_input:disabled]:text-gray-900 [&_select:disabled]:text-gray-900 [&_textarea:disabled]:text-gray-900',
          ]
        )}
      >
      {/* ── Section 1: General Details ── */}
      <div className="mb-5 rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-6 py-3">
          <h3 className="text-sm font-semibold text-gray-700">General Details <span className="ml-2 text-xs font-normal text-gray-400">(Customer Viewable)</span></h3>
        </div>
        <div className="grid gap-4 p-6 sm:grid-cols-2">
          <FormField label="Company" required error={submitted ? errors.companyId : ''}>
            <Select value={form.companyId ?? ''} onChange={(e) => set('companyId', Number(e.target.value))}>
              <option value="">{emptyOption('-- Select Company --')}</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
            </Select>
          </FormField>
          <FormField label="Surface Location" required error={submitted ? errors.siteId : ''}>
            <Select value={form.siteId ?? ''} onChange={(e) => set('siteId', Number(e.target.value))}>
              <option value="">{emptyOption('-- Select Surface Location --')}</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.siteName}</option>)}
            </Select>
          </FormField>
          <FormField label="Well Name" required error={submitted ? errors.wellName : ''}>
            <Input value={form.wellName ?? ''} placeholder={ph('e.g. Edson 13-12-064-18W5')} onChange={(e) => set('wellName', e.target.value)} />
          </FormField>
          <FormField label="UWI" required error={submitted ? errors.wellId : ''}>
            <Input value={form.wellId ?? ''} placeholder={ph('e.g. 100/13-12-064-18W5/00')} onChange={(e) => set('wellId', e.target.value)} />
          </FormField>
          <FormField label="Licence Number" required error={submitted ? errors.licenceNumber : ''}>
            <Input value={form.licenceNumber ?? ''} placeholder={ph('e.g. 0123456')} onChange={(e) => set('licenceNumber', e.target.value)} />
          </FormField>
          <FormField label="Field Name">
            <Input value={form.fieldName ?? ''} placeholder={ph('e.g. Edson')} onChange={(e) => set('fieldName', e.target.value)} />
          </FormField>
          <FormField label="AFE">
            <Input value={form.afe ?? ''} placeholder={ph('e.g. AFE-2025-0042')} onChange={(e) => set('afe', e.target.value)} />
          </FormField>
          <FormField label="Rig">
            <Input value={form.rig ?? ''} placeholder={ph('e.g. Ensign 861')} onChange={(e) => set('rig', e.target.value)} />
          </FormField>
          <FormField label="GTI Job Number">
            <Input value={form.son ?? ''} placeholder={ph('e.g. GTI-12345')} onChange={(e) => set('son', e.target.value)} />
          </FormField>
          <div className="sm:col-span-2 grid grid-cols-3 gap-4">
            <FormField label="Start Bit Depth">
              <Input type="number" value={form.startDepth ?? ''} placeholder={ph('e.g. 0')} onChange={(e) => set('startDepth', Number(e.target.value))} />
            </FormField>
            <FormField label="End Bit Depth">
              <Input type="number" value={form.stopDepth ?? ''} placeholder={ph('e.g. 3500')} onChange={(e) => set('stopDepth', Number(e.target.value))} />
            </FormField>
            <FormField label="Perigon Sensor Offset to Bit">
              <Input type="number" value={form.perigonSensorOffsetToBit ?? ''} placeholder={ph('e.g. 12.5')} onChange={(e) => set('perigonSensorOffsetToBit', e.target.value === '' ? null : Number(e.target.value))} />
            </FormField>
          </div>
          <FormField label="Service Mobilization Date">
            <Input type="date" value={form.serviceDate?.split('T')[0] ?? ''} onChange={(e) => set('serviceDate', e.target.value)} />
          </FormField>
          <FormField label="Service Release Date">
            <Input type="date" value={form.serviceReleaseDate?.split('T')[0] ?? ''} onChange={(e) => set('serviceReleaseDate', e.target.value)} />
          </FormField>
        </div>
      </div>

      {/* ── Section 2: Technical Details ── */}
      {canSeeInternalOnly && (
        <div className="mb-5 rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-6 py-3">
            <h3 className="text-sm font-semibold text-gray-700">Technical Details <span className="ml-2 text-xs font-normal text-gray-400">(Internal Only)</span></h3>
          </div>
          <div className="grid gap-4 p-6 sm:grid-cols-2">
            <FormField label="Perigon Tool Used">
              <Select value={form.toolId ?? ''} onChange={(e) => set('toolId', e.target.value ? Number(e.target.value) : null)}>
                <option value="">{emptyOption(`Select a tool (${tools.length} available)`)}</option>
                {tools.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.toolModel?.model || t.toolModel?.toolModelName || 'Unknown'} — SN: {t.serialNumber}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Bit Size (mm)">
              <Select value={form.bs ?? ''} onChange={(e) => set('bs', e.target.value)}>
                <option value="">{emptyOption('-- Select --')}</option>
                {['156', '159', '171', '200', '216', '222'].map((v) => <option key={v} value={v}>{v}</option>)}
              </Select>
            </FormField>
            <FormField label="Intermediate Casing Point (ICP)">
              <Input value={form.cbl ?? ''} placeholder={ph('e.g. 1500')} onChange={(e) => set('cbl', e.target.value)} />
            </FormField>
            <FormField label="Drilling Fluid Type">
              <Select value={form.dft ?? ''} onChange={(e) => set('dft', e.target.value)}>
                <option value="">{emptyOption('-- Select --')}</option>
                <option value="Water Based">Water Based</option>
                <option value="Oil Based">Oil Based</option>
                <option value="Brine">Brine</option>
              </Select>
            </FormField>
            <FormField label="Drilling Fluid Density">
              <Input value={form.dfd ?? ''} placeholder={ph('e.g. 1050 kg/m³')} onChange={(e) => set('dfd', e.target.value)} />
            </FormField>
            <FormField label="Drilling Fluid Viscosity">
              <Input value={form.dfv ?? ''} placeholder={ph('e.g. 45 s')} onChange={(e) => set('dfv', e.target.value)} />
            </FormField>
            <div className="sm:col-span-2 grid grid-cols-3 gap-4">
              <FormField label="Total Drilling Hours">
                <Input type="number" value={form.tdh ?? ''} placeholder={ph('e.g. 120')} onChange={(e) => set('tdh', e.target.value)} />
              </FormField>
              <FormField label="Total Circulating Hours">
                <Input type="number" value={form.tch ?? ''} placeholder={ph('e.g. 90')} onChange={(e) => set('tch', e.target.value)} />
              </FormField>
              <FormField label="Total Hours Below Rotary">
                <Input type="number" value={form.thbr ?? ''} placeholder={ph('e.g. 75')} onChange={(e) => set('thbr', e.target.value)} />
              </FormField>
            </div>
            <FormField label="Geological Formation" className="sm:col-span-2">
              <Input value={form.frm ?? ''} placeholder={ph('e.g. Montney')} onChange={(e) => set('frm', e.target.value)} />
            </FormField>
            <FormField label="Latitude">
              <Input value={form.latitude ?? ''} placeholder={ph('e.g. 53.5461')} onChange={(e) => set('latitude', e.target.value)} />
            </FormField>
            <FormField label="Longitude">
              <Input value={form.longitude ?? ''} placeholder={ph('e.g. -113.4938')} onChange={(e) => set('longitude', e.target.value)} />
            </FormField>
            <FormField label="Notes" className="sm:col-span-2">
              <textarea
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                rows={3}
                value={form.notes ?? ''}
                placeholder={ph('Anything the team should know about this well…')}
                onChange={(e) => set('notes', e.target.value)}
              />
            </FormField>
          </div>
        </div>
      )}

      {/* ── Section 3: Annotations (edit mode only) ── */}
      {isEdit && canSeeInternalDetails && (
        <div className="mb-5 rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-6 py-3">
            <h3 className="text-sm font-semibold text-gray-700">Annotations</h3>
          </div>
          <div className="p-6">
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 items-end">
              <FormField label="Annotation">
                <Select value={selectedAnnotationId} onChange={(e) => setSelectedAnnotationId(e.target.value)}>
                  <option value="">{emptyOption('Select an annotation')}</option>
                  {annotations.map((a) => <option key={a.id} value={a.id}>{a.shortDescription}</option>)}
                </Select>
              </FormField>
              <FormField label="Depth">
                <Input
                  type="number" min="0.01" step="0.01"
                  value={annotationDepth}
                  onChange={(e) => setAnnotationDepth(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder={ph('Enter depth')}
                />
              </FormField>
              <div className="flex gap-2">
                <Button
                  onClick={addWellAnnotation}
                  disabled={!selectedAnnotationId || annotationDepth === '' || Number(annotationDepth) <= 0}
                >
                  {annotationEditId !== null ? 'Update' : 'Add'}
                </Button>
                {annotationEditId !== null && (
                  <Button variant="secondary" onClick={() => { setAnnotationEditId(null); setSelectedAnnotationId(''); setAnnotationDepth(''); }}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                    <th className="px-4 py-2">Annotation</th>
                    <th className="px-4 py-2">Description</th>
                    <th className="px-4 py-2">Depth</th>
                    {canEdit && <th className="px-4 py-2">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {wellAnnotations.length === 0 ? (
                    <tr><td colSpan={canEdit ? 4 : 3} className="px-4 py-6 text-center text-gray-400">No annotations added</td></tr>
                  ) : wellAnnotations.map((wa) => (
                    <tr key={wa.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium">{wa.shortDescription}</td>
                      <td className="px-4 py-2 text-gray-500">{wa.longDescription}</td>
                      <td className="px-4 py-2">{wa.depth}</td>
                      {canEdit && (
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <button onClick={() => editWellAnnotation(wa.id)} title="Edit" className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all active:scale-95"><Pencil size={12} strokeWidth={2.5} /></button>
                            <button onClick={() => removeWellAnnotation(wa.id)} title="Delete" className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-all active:scale-95"><Trash2 size={12} strokeWidth={2.5} /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      </fieldset>{/* end left column */}

      {/* Right column: file sections (sticky, V2 order: 3D Models → Client Downloads → LAS Files) */}
      <div className="min-w-0 lg:sticky lg:top-20 self-start space-y-4">

      {/* ── Create well from LAS (create mode) ── */}
      {!isEdit && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Create Well from LAS <span className="ml-1 text-xs font-normal text-gray-400">(Internal Only)</span></h3>
            <input
              ref={createLasFileInputRef}
              type="file"
              accept=".las"
              className="hidden"
              onChange={(e) => handleCreateWellFromLas(e.target.files?.[0])}
            />
            <Button
              onClick={() => {
                if (creatingFromLas) return;
                if (!form.companyId) { showToast('Please select a Company first', 'error'); return; }
                createLasFileInputRef.current?.click();
              }}
              disabled={creatingFromLas}
              className="text-xs px-3 py-1.5"
            >
              <Upload size={12} /> {creatingFromLas ? 'Uploading...' : 'Upload LAS'}
            </Button>
          </div>
          {creatingFromLas && (
            <div className="px-4 pt-3 pb-1">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                  {lasCreatePhase === 'uploading' ? (
                    <div className="h-full bg-primary-500 rounded-full transition-all duration-300" style={{ width: `${lasCreateProgress}%` }} />
                  ) : (
                    <div className="h-full w-full bg-primary-500 rounded-full animate-pulse" />
                  )}
                </div>
                {lasCreatePhase === 'uploading' ? (
                  <>
                    <span className="text-xs text-gray-500 w-10 text-right">{lasCreateProgress}%</span>
                    <button onClick={() => lasCreateAbortRef.current?.abort()} className="text-xs text-red-500 hover:text-red-700" title="Cancel upload">
                      Cancel
                    </button>
                  </>
                ) : (
                  <span className="text-xs text-gray-500 whitespace-nowrap">Processing…</span>
                )}
              </div>
              <p className="mt-1.5 mb-0 text-xs text-gray-500">
                {lasCreatePhase === 'uploading'
                  ? 'Uploading LAS file…'
                  : 'Parsing LAS and creating well(s). This can take a minute — please keep this page open.'}
              </p>
            </div>
          )}
          <div className="p-4">
            <div
              onClick={() => {
                if (creatingFromLas) return;
                if (!form.companyId) { showToast('Please select a Company first', 'error'); return; }
                createLasFileInputRef.current?.click();
              }}
              onDragOver={(e) => { e.preventDefault(); if (!creatingFromLas) setIsLasDragOver(true); }}
              onDragLeave={() => setIsLasDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsLasDragOver(false);
                if (creatingFromLas) return;
                const file = e.dataTransfer.files?.[0];
                handleCreateWellFromLas(file);
              }}
              className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors cursor-pointer ${
                isLasDragOver ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
              } ${creatingFromLas ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <Upload size={20} className="text-gray-400" />
              <p className="text-xs text-gray-600 mb-0">
                {creatingFromLas
                  ? (lasCreatePhase === 'uploading' ? `Uploading… ${lasCreateProgress}%` : 'Processing LAS file…')
                  : (form.companyId ? 'Drag and drop your LAS file here, or click to browse' : 'Select a Company first')}
              </p>
            </div>
          </div>
        </div>
      )}

      {isEdit && <>

      {/* ── 3D Models (admin only) ── */}
      {canSeeInternalDetails && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-700">3D Models</h3>
          </div>
          <div className="p-4">
            {visiblePlyFiles.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-3">No 3D models available</p>
            ) : (
              <div className="space-y-1.5">
                {visiblePlyFiles.map((f) => {
                  const actualName = `${f.wellName}_${f.fromLength}m-${f.toLength}m_Actual_${f.id}.ply`;
                  const perfectName = `${f.wellName}_${f.fromLength}m-${f.toLength}m_Perfect_${f.id}.ply`;
                  return (
                    <div key={f.id}>
                      {f.hasActualFile !== false && (
                      <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 mb-1">
                        <img src="/icons/ico-ply.png" alt="" className="w-8 h-8 shrink-0" />
                        <p className="text-sm text-gray-700 flex-1 min-w-0 mb-0 break-all">{actualName}</p>
                        <div className="flex gap-1.5 shrink-0">
                          <button onClick={() => open3DModel(f)} title="Open 3D Viewer" className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-500 text-white transition-all hover:bg-sky-600 hover:shadow-md active:scale-95">
                            <Box size={16} strokeWidth={2.25} />
                          </button>
                          <button onClick={() => handlePlyDownload(f, 'A')} disabled={downloadingPly !== null} title="Download" className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-700 hover:shadow-md active:scale-95 disabled:opacity-50">
                            <Download size={16} strokeWidth={2.25} />
                          </button>
                        </div>
                      </div>
                      )}
                      {f.hasPerfectFile !== false && (
                      <div className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 mb-1">
                        <img src="/icons/ico-ply.png" alt="" className="w-8 h-8 shrink-0" />
                        <p className="text-sm text-gray-700 flex-1 min-w-0 mb-0 break-all">{perfectName}</p>
                        <div className="flex gap-1.5 shrink-0">
                          <button onClick={() => open3DModel(f)} title="Open 3D Viewer" className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-500 text-white transition-all hover:bg-sky-600 hover:shadow-md active:scale-95">
                            <Box size={16} strokeWidth={2.25} />
                          </button>
                          <button onClick={() => handlePlyDownload(f, 'P')} disabled={downloadingPly !== null} title="Download" className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-700 hover:shadow-md active:scale-95 disabled:opacity-50">
                            <Download size={16} strokeWidth={2.25} />
                          </button>
                        </div>
                      </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Client Downloads ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-4 py-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Client Downloads <span className="ml-1 text-xs font-normal text-gray-400">(Customer Viewable)</span></h3>
          {canEdit && (
            <>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
              <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="text-xs px-3 py-1.5">
                <Upload size={12} /> {uploading ? 'Uploading...' : 'Upload File'}
              </Button>
            </>
          )}
        </div>
        {uploading && (
          <div className="px-4 pb-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full bg-primary-500 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
              </div>
              <span className="text-xs text-gray-500 w-10 text-right">{uploadProgress}%</span>
              <button onClick={() => uploadAbortRef.current?.abort()} className="text-xs text-red-500 hover:text-red-700" title="Cancel upload">
                Cancel
              </button>
            </div>
          </div>
        )}
        <div className="p-4">
          {uploadedFiles.length === 0 && !uploading ? (
            <p className="text-center text-sm text-gray-400 py-3">No files available</p>
          ) : (
            <div className="space-y-1.5">
              {uploadedFiles.map((f) => (
                  <div key={f.fileName} className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                    <img src={getFileIcon(f.fileName)} alt="" className="w-8 h-8 shrink-0" />
                    <p className="text-sm text-gray-700 flex-1 min-w-0 mb-0 break-all">{f.fileName}</p>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => handleDownload(f.fileName)} disabled={downloadingFile === f.fileName} title="Download" className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-700 hover:shadow-md active:scale-95 disabled:opacity-50">
                        <Download size={16} strokeWidth={2.25} />
                      </button>
                      {canEdit && (
                        <button onClick={() => handleDeleteFile(f.fileName)} title="Delete" className="flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white transition-all hover:bg-red-700 hover:shadow-md active:scale-95">
                          <Trash2 size={16} strokeWidth={2.25} />
                        </button>
                      )}
                    </div>
                  </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── LAS Files (admin only) ── */}
      {canSeeInternalOnly && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">LAS Files <span className="ml-1 text-xs font-normal text-gray-400">(Internal Only)</span></h3>
            {/* Upload LAS button hidden per request. Input + handler kept wired for easy restore. */}
            {canEdit && <input ref={lasFileInputRef} type="file" accept=".las" className="hidden" onChange={handleLasUpload} />}
          </div>
          {uploadingLas && (
            <div className="px-4 pb-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div className="h-full bg-primary-500 rounded-full transition-all duration-300" style={{ width: `${lasUploadProgress}%` }} />
                </div>
                <span className="text-xs text-gray-500 w-10 text-right">{lasUploadProgress}%</span>
                <button onClick={() => lasUploadAbortRef.current?.abort()} className="text-xs text-red-500 hover:text-red-700" title="Cancel upload">
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className="p-4">
            {lasFiles.length === 0 && !uploadingLas ? (
              <p className="text-center text-sm text-gray-400 py-3">No LAS files uploaded</p>
            ) : (
              <div className="space-y-1.5">
                {lasFiles.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                    <img src="/icons/ico-las.png" alt="" className="w-8 h-8 shrink-0" />
                    <p className="text-sm text-gray-700 flex-1 min-w-0 mb-0 break-all">{f.fileName}</p>
                    <div className="flex gap-1.5 shrink-0">
                      <button onClick={() => handleLasDownload(f)} disabled={downloadingFile === f.fileName} title="Download LAS" className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white transition-all hover:bg-primary-700 hover:shadow-md active:scale-95 disabled:opacity-50">
                        <Download size={16} strokeWidth={2.25} />
                      </button>
                      {canEdit && (
                        <button onClick={() => handleLasDelete(f)} title="Delete LAS" className="flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white transition-all hover:bg-red-700 hover:shadow-md active:scale-95">
                          <Trash2 size={16} strokeWidth={2.25} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      </>}
      </div>{/* end right column */}
      </div>{/* end two-column grid */}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
